import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useStore } from 'vuex'

// スクロール処理のthrottle実装
function throttle(func, wait) {
  let timeout = null
  let lastTime = 0

  return function executedFunction(...args) {
    const now = Date.now()
    
    if (lastTime && now < lastTime + wait) {
      clearTimeout(timeout)
      timeout = setTimeout(() => {
        lastTime = now
        func.apply(this, args)
      }, wait - (now - lastTime))
    } else {
      lastTime = now
      func.apply(this, args)
    }
  }
}

export function useNavbarState() {
  const store = useStore()
  const router = useRouter()
  const route = useRoute()

  // リアクティブな状態管理
  const isScrolled = ref(false)
  const scrollPosition = ref(0)
  const isMobileMenuOpen = ref(false)
  const activeDropdownId = ref(null)
  const lastScrollDirection = ref('up')
  const previousScrollPosition = ref(0)

  // 現在のルート情報
  const activeRoute = computed(() => route.path)

  // スクロール処理（パフォーマンス最適化済み）
  const handleScroll = throttle(() => {
    const currentScrollPosition = window.scrollY
    
    // スクロール状態の更新
    scrollPosition.value = currentScrollPosition
    isScrolled.value = currentScrollPosition > 50
    
    // スクロール方向の検出
    if (currentScrollPosition > previousScrollPosition.value) {
      lastScrollDirection.value = 'down'
    } else {
      lastScrollDirection.value = 'up'
    }
    
    previousScrollPosition.value = currentScrollPosition
    
    // Vuexストアに状態を同期（後でnavbarモジュールを追加）
    if (store.state.navbar) {
      store.commit('navbar/SET_SCROLL_STATE', {
        isScrolled: isScrolled.value,
        scrollPosition: currentScrollPosition
      })
    }
  }, 100)

  // モバイルメニューの切り替え
  const toggleMobileMenu = () => {
    isMobileMenuOpen.value = !isMobileMenuOpen.value
    
    // body要素のスクロールロック
    if (isMobileMenuOpen.value) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    
    // Vuexストアに状態を同期
    if (store.state.navbar) {
      store.commit('navbar/SET_MOBILE_MENU_STATE', isMobileMenuOpen.value)
    }
  }

  // モバイルメニューを閉じる
  const closeMobileMenu = () => {
    isMobileMenuOpen.value = false
    document.body.style.overflow = ''
    
    if (store.state.navbar) {
      store.commit('navbar/SET_MOBILE_MENU_STATE', false)
    }
  }

  // ドロップダウンの切り替え
  const toggleDropdown = (dropdownId) => {
    if (activeDropdownId.value === dropdownId) {
      activeDropdownId.value = null
    } else {
      activeDropdownId.value = dropdownId
    }
    
    if (store.state.navbar) {
      store.commit('navbar/SET_ACTIVE_DROPDOWN', activeDropdownId.value)
    }
  }

  // ドロップダウンを閉じる
  const closeDropdown = () => {
    activeDropdownId.value = null
    
    if (store.state.navbar) {
      store.commit('navbar/SET_ACTIVE_DROPDOWN', null)
    }
  }

  // ルートがアクティブかどうかを判定
  const isRouteActive = (routePath) => {
    if (routePath === '/') {
      return route.path === '/'
    }
    return route.path.startsWith(routePath)
  }

  // 外部リンクかどうかを判定
  const isExternalLink = (path) => {
    return path.startsWith('http://') || path.startsWith('https://') || path.startsWith('#')
  }

  // ナビゲーション処理
  const navigateTo = (path) => {
    if (isExternalLink(path)) {
      if (path.startsWith('#')) {
        // アンカーリンクの場合
        const element = document.querySelector(path)
        if (element) {
          // 現在のページに要素が存在する場合
          element.scrollIntoView({ behavior: 'smooth' })
        } else {
          // 現在のページに要素が存在しない場合、トップページに遷移してからスクロール
          if (route.path !== '/') {
            router.push('/' + path).then(() => {
              // 遷移完了後、少し待ってから要素を探してスクロール
              setTimeout(() => {
                const targetElement = document.querySelector(path)
                if (targetElement) {
                  targetElement.scrollIntoView({ behavior: 'smooth' })
                }
              }, 100)
            })
          }
        }
      } else {
        // 外部リンクの場合
        window.open(path, '_blank', 'noopener')
      }
    } else {
      // 内部リンクの場合
      router.push(path)
    }
    
    // モバイルメニューを閉じる
    closeMobileMenu()
    
    // ドロップダウンを閉じる
    closeDropdown()
  }

  // ESCキーでメニューを閉じる
  const handleEscKey = (event) => {
    if (event.key === 'Escape') {
      closeMobileMenu()
      closeDropdown()
    }
  }

  // クリックイベントでドロップダウンを閉じる
  const handleClickOutside = (event) => {
    const navbar = document.querySelector('.enhanced-navbar')
    if (navbar && !navbar.contains(event.target)) {
      closeDropdown()
    }
  }

  // ルート変更時の処理
  watch(route, () => {
    closeMobileMenu()
    closeDropdown()
  })

  // ライフサイクルフック
  onMounted(() => {
    // イベントリスナーの登録
    window.addEventListener('scroll', handleScroll, { passive: true })
    document.addEventListener('keydown', handleEscKey)
    document.addEventListener('click', handleClickOutside)
    
    // 初期スクロール位置の取得
    handleScroll()
  })

  onUnmounted(() => {
    // イベントリスナーの削除
    window.removeEventListener('scroll', handleScroll)
    document.removeEventListener('keydown', handleEscKey)
    document.removeEventListener('click', handleClickOutside)
    
    // body要素のスクロールロックを解除
    document.body.style.overflow = ''
  })

  return {
    // 状態
    isScrolled,
    scrollPosition,
    isMobileMenuOpen,
    activeDropdownId,
    activeRoute,
    lastScrollDirection,
    
    // メソッド
    handleScroll,
    toggleMobileMenu,
    closeMobileMenu,
    toggleDropdown,
    closeDropdown,
    isRouteActive,
    isExternalLink,
    navigateTo
  }
}